/**
 * Utility functions for formatting various data types
 */

/**
 * Formats a number into a shortened currency format with suffix (K, M, B)
 *
 * @param num - The number to format
 * @returns Formatted string with appropriate suffix
 *
 * Examples:
 * - 1234 -> $1.2K
 * - 1234567 -> $1.2M
 * - 1234567890 -> $1.2B
 */
export const formatBigMoney = (num: number): string => {
  if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`
  return `$${num.toFixed(2)}`
}

/**
 * Formats a number as a currency with comma separators and two decimal places
 *
 * @param amount - The number to format
 * @returns Formatted currency string
 *
 * Example:
 * - 1234.56 -> $1,234.56
 */
export const formatCurrency = (amount?: number): string => {
  if (amount === null || amount === undefined) return '$0.00'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount)
}

/**
 * Formats a rate value with exactly 5 decimal places without thousands separators
 *
 * @param amount - The rate to format
 * @returns Formatted rate string
 *
 * Example:
 * - 12345.67890 -> $12345.67890
 */
export const formatRate = (amount?: number): string => {
  if (amount === null || amount === undefined) return '$0.00000'
  return `$${amount.toFixed(5)}`
}
